import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export default function ChatPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const userId = Number(localStorage.getItem("user_id") || 0);
  const cachedTarget = localStorage.getItem("current_chat_user");
  const target =
    location.state?.user || (cachedTarget ? JSON.parse(cachedTarget) : null);

  const [ws, setWs] = useState(null);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [checkingFriendship, setCheckingFriendship] = useState(true);
  const [messageQuota, setMessageQuota] = useState(null);

  const bottomRef = useRef(null);
  const scrollToBottom = () =>
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });

  const API_BASE = process.env.REACT_APP_API_BASE || "";

  // 标记已读
  useEffect(() => {
    if (!userId || !target?.id) return;

    fetch(`${API_BASE}/api/messages/read`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        current_user_id: userId,
        target_user_id: Number(target.id),
      }),
    }).catch((err) => console.error("标记已读失败:", err));
  }, [userId, target, API_BASE]);

  // 保存当前聊天对象
  useEffect(() => {
    if (location.state?.user) {
      localStorage.setItem(
        "current_chat_user",
        JSON.stringify(location.state.user)
      );
    }
  }, [location.state]);

  // 离开页面清理
  useEffect(() => {
    return () => {
      localStorage.removeItem("current_chat_user");
      ws?.close();
    };
  }, [ws]);

  // 好友关系检查
  useEffect(() => {
    if (!userId) {
      navigate("/login");
      return;
    }
    if (!target?.id) {
      alert("缺少聊天对象");
      navigate("/center/friends");
      return;
    }

    const checkFriendship = async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/friendship-status?current_user_id=${userId}&target_user_id=${target.id}`
        );
        const data = await res.json();
        if (!res.ok) {
          alert("无法进入聊天");
          navigate(`/user/${target.id}`);
          return;
        }
        if (data.blocked_by_me || data.blocked_me) {
          alert("存在拉黑关系，暂时不能聊天");
          navigate(`/user/${target.id}`);
          return;
        }
        if (!data.is_friend) {
          alert("你们还不是好友，请先发送好友申请");
          navigate(`/user/${target.id}`);
          return;
        }
        setCheckingFriendship(false);
      } catch (err) {
        console.error("好友关系校验失败:", err);
        alert("无法进入聊天");
        navigate("/center/friends");
      }
    };

    checkFriendship();
  }, [userId, target, navigate, API_BASE]);

  useEffect(() => {
    if (checkingFriendship || !userId) return;

    const loadQuota = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/message-limits?user_id=${userId}`);
        const data = await res.json();
        if (res.ok) {
          setMessageQuota(data);
        }
      } catch (err) {
        console.error("加载聊天额度失败:", err);
      }
    };

    loadQuota();
  }, [checkingFriendship, userId, API_BASE]);

  // 加载聊天记录
  useEffect(() => {
    if (checkingFriendship || !userId || !target?.id) return;

    const loadMessages = async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/messages?current_user_id=${userId}&target_user_id=${target.id}`
        );
        const data = await res.json();
        if (Array.isArray(data)) {
          setMessages(
            data.map((item) => ({
              id: item.id,
              from: Number(item.from),
              text: item.message,
              created_at: item.created_at,
            }))
          );
        }
      } catch (err) {
        console.error("加载聊天记录失败:", err);
      }
    };

    loadMessages();
  }, [checkingFriendship, userId, target, API_BASE]);

  // WebSocket
  useEffect(() => {
    if (checkingFriendship || !userId) return;

    const protocol = window.location.protocol === "https:" ? "wss" : "ws";
    const runtimeWs = typeof window !== "undefined" ? window.__RUNTIME_CONFIG__?.WS_URL : "";
    const wsBase =
      runtimeWs ||
      process.env.REACT_APP_WS_URL ||
      (process.env.REACT_APP_WS_HOST
        ? `${protocol}://${process.env.REACT_APP_WS_HOST}${
            process.env.REACT_APP_WS_PORT ? `:${process.env.REACT_APP_WS_PORT}` : ""
          }`
        : `${protocol}://${window.location.host}`);

    const socket = new WebSocket(`${wsBase}/ws/${userId}`);

    socket.onopen = () => console.log("WebSocket连接成功");
    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.error) {
          alert(data.error);
          fetch(`${API_BASE}/api/message-limits?user_id=${userId}`)
            .then((res) => res.json())
            .then((quota) => setMessageQuota(quota))
            .catch(() => {});
          return;
        }
        if (messageQuota && !messageQuota.is_verified && Number(data.from) === userId) {
          setMessageQuota((prev) =>
            prev
              ? {
                  ...prev,
                  used: (prev.used || 0) + 1,
                  remaining: Math.max(0, (prev.remaining || 0) - 1),
                }
              : prev
          );
        }
        setMessages((prev) => [
          ...prev,
          { id: data.id || Date.now(), from: Number(data.from), text: data.message, created_at: data.created_at },
        ]);
      } catch (err) {
        console.error("WS 数据解析错误:", err);
      }
    };
    socket.onclose = () => console.log("WebSocket关闭");
    socket.onerror = (err) => console.error("WebSocket错误:", err);

    setWs(socket);
    return () => socket.close();
  }, [checkingFriendship, userId]);

  useEffect(scrollToBottom, [messages]);

  const quotaExhausted =
    messageQuota &&
    !messageQuota.is_verified &&
    messageQuota.remaining != null &&
    messageQuota.remaining <= 0;

  // 发送消息
  const sendMessage = () => {
    if (quotaExhausted) {
      alert(
        `未认证会员本周聊天额度已用完（${messageQuota.limit}条/周），请上传身份证完成认证后不限条数`
      );
      return;
    }
    if (!input.trim() || !ws || ws.readyState !== WebSocket.OPEN || !target?.id) return;

    ws.send(
      JSON.stringify({
        to: Number(target.id),
        message: input.trim(),
      })
    );
    setInput("");
  };

  const handleKeyDown = (e) => e.key === "Enter" && sendMessage();

  if (checkingFriendship) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-pink-50 to-purple-50">
        <div className="text-gray-500">正在检查好友关系...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 flex flex-col">
      <div className="bg-white shadow p-4 text-center font-bold">
        💬 和 {target?.name || "好友"} 聊天
      </div>

      {messageQuota && !messageQuota.is_verified && (
        <div className="bg-amber-50 border-b border-amber-100 px-4 py-2 text-sm text-amber-800 text-center">
          未认证会员本周还可发送 {messageQuota.remaining ?? 0} / {messageQuota.limit ?? 0} 条消息。
          <button
            type="button"
            onClick={() => navigate("/center/verify")}
            className="ml-2 text-pink-600 font-bold underline"
          >
            去认证
          </button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map((m, i) => (
          <div
            key={m.id || i}
            className={`flex ${m.from === userId ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`px-4 py-2 rounded-2xl max-w-[70%] ${
                m.from === userId ? "bg-pink-500 text-white" : "bg-white shadow"
              }`}
            >
              {m.text}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <div className="bg-white p-3 flex gap-2 border-t">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={quotaExhausted ? "本周额度已用完，请先完成认证" : "输入消息..."}
          disabled={quotaExhausted}
          className="flex-1 border p-2 rounded-full px-4 disabled:bg-gray-100"
        />
        <button
          onClick={sendMessage}
          disabled={quotaExhausted}
          className="bg-pink-500 text-white px-5 rounded-full disabled:opacity-50"
        >
          发送
        </button>
      </div>
    </div>
  );
}